import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';

import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { CampoObrigatorioStruct } from '../../../../../shared/services/structs/financas/campo-obrigatorio.struct';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ContasReceberService } from '../../../../../shared/services/apis/financas/contas-receber.service';

export interface ContasReceberArquivoStruct {
  idContasReceberAnexo?: number;
  nomeArquivo: string;
  tamanhoBytes: number;
  urlArquivo?: string;
  base64?: string;
}

@Component({
  selector: 'app-contas-receber-complementares',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    SelectPesquisavelComponent,
  ],
  templateUrl: './contas-receber-complementares.component.html',
  styleUrl: './contas-receber-complementares.component.css',
})
export class ContasReceberComplementaresComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Input() camposObrigatorios: CampoObrigatorioStruct[] = [];

  // Tags do documento (lista disponível + control no form raiz). mostrarTags controla a visibilidade
  // (escondido ao editar documentos com múltiplas parcelas — edição é feita por parcela).
  @Input() tagsControl!: FormControl;
  @Input() listaTag: any[] = [];
  @Input() mostrarTags: boolean = true;
  /** Documento em leitura (gerado pelo pedido de venda): só baixa os anexos, sem enviar nem remover. */
  @Input() isSomenteLeitura: boolean = false;

  // Upload Definitions
  public anexos: ContasReceberArquivoStruct[] = [];
  public readonly MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  public readonly MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10MB
  public isHovering: boolean = false;

  get complementosGroup(): FormGroup {
    return this.parentForm.get('complementos') as FormGroup;
  }

  public isObrigatorio(nomeTecnico: string): boolean {
    return this.camposObrigatorios.some(c => c.nomeTecnico === nomeTecnico && c.isObrigatorio);
  }

  constructor(
    private alertService: AlertService,
    private dialog: MatDialog,
    private contasReceberService: ContasReceberService
  ) {}

  ngOnInit(): void {}

  // --- Upload Drag & Drop Core ---
  public toggleHover(event: boolean): void {
    this.isHovering = event;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = false;
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFiles(input.files);
      input.value = ''; // Reset the input so the same file can be selected again
    }
  }

  private processFiles(files: FileList): void {
    let currentTotalSize = this.anexos.reduce((total, file) => total + file.tamanhoBytes, 0);
    let newFilesSize = 0;

    for (let i = 0; i < files.length; i++) {
      newFilesSize += files[i].size;
    }

    if (currentTotalSize + newFilesSize > this.MAX_TOTAL_SIZE) {
      this.alertService.show('Aviso', 'O tamanho total dos anexos não pode exceder 10MB.', AlertType.warning);
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > this.MAX_FILE_SIZE) {
        this.alertService.show(
          'Aviso',
          `O arquivo "${file.name}" excede o limite de 2MB.`,
          AlertType.warning
        );
        continue;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64Data = e.target.result.split(',')[1];
        this.anexos.push({
          nomeArquivo: file.name,
          tamanhoBytes: file.size,
          base64: base64Data,
        });
      };
      reader.readAsDataURL(file);
    }
  }

  public removeAnexo(index: number): void {
    this.anexos.splice(index, 1);
  }

  public formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  public downloadAnexo(index: number): void {
    const anexo = this.anexos[index];
    if (!anexo.urlArquivo) {
      this.alertService.show('Aviso', 'Este arquivo ainda não foi salvo no servidor.', AlertType.warning);
      return;
    }

    this.contasReceberService.getArquivoContasReceber(anexo.urlArquivo).subscribe({
      next: (res: any) => {
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao baixar o arquivo.',
            AlertType.error
          );
          return;
        }
        const a = document.createElement('a');
        a.href = res.urlArquivo;
        a.download = anexo.nomeArquivo;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao baixar o arquivo.', AlertType.error);
      },
    });
  }

  limparAnexos(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmação',
        description: 'Deseja realmente remover todos os anexos?',
        buttonConfirm: 'Sim',
        buttonClose: 'Não',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.anexos = [];
      }
    });
  }
}
